// src/app/modules/Technical/components/Machinery/MachineryUploadModal.tsx
import React, {FC, useState, useEffect, useMemo} from 'react'
import {useAuth} from '../../../auth'
import {getVesselList} from '../../../Management/core/_requests'
import {
  downloadMachineryTemplate,
  previewMachineryUpload,
  fetchMachineryPreviewRows,
  startMachineryUploadJob,
  fetchMachineryUploadJob,
  fetchActiveMachineryUploadJob,
  cancelMachineryUploadJob,
  BulkUploadResult,
  BulkUploadError,
  MachineryUploadPreview,
  PreviewRow,
  MachineryUploadPreviewRowsPage,
  MachineryUploadJob,
} from '../../core/_requests'
import type {Vessel} from '../../../Management/core/_models'
import {toast} from 'react-toastify'
import {KTSVG} from '../../../../../_metronic/helpers'
import {getAccessibleVessels, isVesselAssignedUserOnly} from '../../core/technicalScopeUtils'
import {TECH_PERM} from '../../core/technicalPermissions'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const isWarningMessage = (message?: string | null): boolean => {
  if (!message) return false
  return message.toLowerCase().includes('warning:')
}
const STORAGE_KEY = 'machineryUploadJobId'
const UPLOAD_JOB_POLL_INTERVAL_MS = 20000

const formatEta = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}m ${secs}s`
}

interface MachineryUploadModalProps {
  visible: boolean
  onClose: () => void
  onSuccess?: () => void
}

type UploadStep = 'UPLOAD' | 'PREVIEW' | 'RESULT'

export const MachineryUploadModal: FC<MachineryUploadModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const {currentUser, isSuperAdmin, hasPermission} = useAuth()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const canImportMachinery = hasPermission(TECH_PERM.IMPORT_MACHINERY)

  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [currentStep, setCurrentStep] = useState<UploadStep>('UPLOAD')
  const [previewing, setPreviewing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<MachineryUploadPreview | null>(null)
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
  const [previewRowsPage, setPreviewRowsPage] = useState(1)
  const [previewRowsTotal, setPreviewRowsTotal] = useState(0)
  const [previewRowsLoading, setPreviewRowsLoading] = useState(false)
  const previewRowsPageSize = 20
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null)
  const [uploadJob, setUploadJob] = useState<MachineryUploadJob | null>(null)
  const [showResultErrors, setShowResultErrors] = useState(false)
  const [showJobErrors, setShowJobErrors] = useState(false)
  const isUploadActive = uploadJob?.status === 'QUEUED' || uploadJob?.status === 'RUNNING'
  const lastSuccessJobIdRef = React.useRef<number | null>(null)

  const resetUploadState = () => {
    localStorage.removeItem(STORAGE_KEY)
    setUploadJob(null)
    setUploadResult(null)
    setPreview(null)
    setPreviewRows([])
    setPreviewRowsPage(1)
    setPreviewRowsTotal(0)
    setSelectedFile(null)
    setCurrentStep('UPLOAD')
  }

  useEffect(() => {
    if (!visible) {
      // Reset state when modal closes
      setCurrentStep('UPLOAD')
      setSelectedFile(null)
      setPreview(null)
      setPreviewRows([])
      setPreviewRowsPage(1)
      setPreviewRowsTotal(0)
      setUploadResult(null)
      setUploadJob(null)
      setSelectedVesselId(null)
      setShowResultErrors(false)
      setShowJobErrors(false)
    } else {
      loadVessels()
      loadActiveUploadJob()
    }
  }, [visible])

  useEffect(() => {
    if (!uploadJob?.id) return
    const isActive = uploadJob.status === 'QUEUED' || uploadJob.status === 'RUNNING'
    if (!isActive) return

    const interval = window.setInterval(async () => {
      try {
        const updated = await fetchMachineryUploadJob(uploadJob.id as number)
        setUploadJob(updated)
      } catch (error) {
        // ignore polling errors
      }
    }, UPLOAD_JOB_POLL_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [uploadJob?.id, uploadJob?.status])

  useEffect(() => {
    if (!uploadJob?.id) return
    if (uploadJob.status !== 'SUCCESS') return
    if (lastSuccessJobIdRef.current === uploadJob.id) return
    lastSuccessJobIdRef.current = uploadJob.id
    onSuccess?.()
  }, [uploadJob?.id, uploadJob?.status, onSuccess])

  const uploadVessel = uploadJob?.vesselId
    ? vessels.find((v) => v.id === uploadJob.vesselId)
    : selectedVesselId
    ? vessels.find((v) => v.id === selectedVesselId)
    : undefined
  const uploadVesselLabel = uploadVessel?.fleet_name
    ? `${uploadVessel.fleet_name} (ID ${uploadVessel.id})`
    : uploadJob?.vesselId
    ? `Vessel ID ${uploadJob.vesselId}`
    : selectedVesselId
    ? `Vessel ID ${selectedVesselId}`
    : 'this vessel'
  const inProgressMessage = `Upload in progress for ${uploadVesselLabel}. This is running in the background. You can close this modal and continue other work, then open Machinery Bulk Upload later to check status.`
  const uploadJobErrors = (uploadJob?.errors || []).filter(
    (error) => !isWarningMessage(error.message)
  )
  const uploadResultErrors = (uploadResult?.errors || []).filter(
    (error) => !isWarningMessage(error.message)
  )
  const previewErrorRows = useMemo(
    () => (preview?.errors || []).filter((error) => !isWarningMessage(error.message)),
    [preview?.errors]
  )
  const previewWarnings = useMemo(
    () =>
      (preview?.warnings && preview.warnings.length > 0
        ? preview.warnings
        : (preview?.errors || []).filter((error) => isWarningMessage(error.message))) || [],
    [preview?.errors, preview?.warnings]
  )

  const loadActiveUploadJob = async () => {
    try {
      const storedId = localStorage.getItem(STORAGE_KEY)
      if (storedId) {
        try {
          const storedJob = await fetchMachineryUploadJob(Number(storedId))
          setUploadJob(storedJob)
          setCurrentStep('RESULT')
          return
        } catch (error) {
          localStorage.removeItem(STORAGE_KEY)
        }
      }
      const active = await fetchActiveMachineryUploadJob()
      if (active?.id) {
        setUploadJob(active)
        setCurrentStep('RESULT')
        localStorage.setItem(STORAGE_KEY, String(active.id))
      }
    } catch (error) {
      // ignore
    }
  }

  const loadVessels = async () => {
    try {
      const vesselList = await getVesselList()
      const vesselsForUser = getAccessibleVessels(vesselList, currentUser, isSuperAdmin)

      setVessels(vesselsForUser)
      if (isVesselOnlyUser && currentUser?.vessel?.id) {
        setSelectedVesselId(currentUser.vessel.id)
      } else if (!isVesselOnlyUser && vesselsForUser.length > 0) {
        setSelectedVesselId((prev) => prev ?? vesselsForUser[0].id)
      }
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    const file = e.target.files?.[0]
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv']
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!validExtensions.includes(extension)) {
        toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file')
        return
      }
      setSelectedFile(file)
      setPreview(null)
      setUploadResult(null)
      setCurrentStep('UPLOAD')
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadMachineryTemplate()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'machinery_upload_template.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Template downloaded successfully')
    } catch (error: any) {
      console.error('Error downloading template:', error)
      toast.error('Failed to download template')
    }
  }

  const handlePreview = async () => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    if (!selectedFile) {
      toast.error('Please select a file')
      return
    }
    if (!selectedVesselId) {
      toast.error('Please select a vessel')
      return
    }

    setPreviewing(true)
    try {
      const previewData = await previewMachineryUpload(selectedFile, selectedVesselId)
      setPreview(previewData)
      setPreviewRows(previewData.rows || [])
      setPreviewRowsPage(previewData.rowsPage || 1)
      setPreviewRowsTotal(previewData.rowsTotal || (previewData.rows ? previewData.rows.length : 0))
      setCurrentStep('PREVIEW')

      // Show warning if there are errors in preview
      if (previewData.errorRows > 0) {
        toast.warning(
          `Preview completed with ${previewData.errorRows} error(s). Please review the errors below before importing.`,
          {autoClose: 5000}
        )
      } else if (previewData.totalRows > 0) {
        toast.success(`Preview successful: ${previewData.validRows} valid row(s) found.`, {
          autoClose: 3000,
        })
      }
    } catch (error: any) {
      console.error('Error previewing file:', error)

      // Extract detailed error message from response
      let errorMessage = 'Failed to preview file'

      if (error.response) {
        // Backend returned an error response
        const responseData = error.response.data

        if (
          responseData?.errors &&
          Array.isArray(responseData.errors) &&
          responseData.errors.length > 0
        ) {
          // Multiple errors from backend
          const firstError = responseData.errors[0]
          errorMessage = firstError.message || firstError.error || 'Validation errors found in file'

          // If there are multiple errors, show count
          if (responseData.errors.length > 1) {
            errorMessage += ` (and ${responseData.errors.length - 1} more error(s))`
          }
        } else if (responseData?.message) {
          // Single error message from backend
          errorMessage = responseData.message
        } else if (responseData?.error) {
          errorMessage = responseData.error
        } else if (typeof responseData === 'string') {
          errorMessage = responseData
        } else {
          errorMessage = `Server error: ${error.response.status} ${error.response.statusText || ''}`
        }
      } else if (error.message) {
        // Network or other error
        if (error.message.includes('Network Error') || error.message.includes('ECONNREFUSED')) {
          errorMessage =
            'Cannot connect to server. Please check your internet connection and try again.'
        } else if (error.message.includes('timeout')) {
          errorMessage =
            'Request timed out. The file may be too large. Please try again or contact support.'
        } else {
          errorMessage = 'Upload failed. Please try again or contact support.'
        }
      }

      toast.error(errorMessage, {autoClose: 6000})

      // Also log full error for debugging
      console.error('Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      })
    } finally {
      setPreviewing(false)
    }
  }

  const handleConfirmUpload = async () => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    if (!selectedFile || !selectedVesselId) return

    setUploading(true)
    try {
      const job = await startMachineryUploadJob(selectedFile, selectedVesselId)
      setUploadJob(job)
      setUploadResult(null)
      setCurrentStep('RESULT')
      if (job?.id) {
        localStorage.setItem(STORAGE_KEY, String(job.id))
      }
      toast.info('Upload started in background. You can continue working while it completes.')
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleCancelUpload = async () => {
    if (!uploadJob?.id) {
      onClose()
      return
    }
    if (isUploadActive) {
      const confirmed = window.confirm(
        'Cancel the upload in progress? All rows inserted by this running upload will be rolled back. Do you want to continue?'
      )
      if (!confirmed) return
      try {
        const cancelled = await cancelMachineryUploadJob(uploadJob.id as number)
        setUploadJob(cancelled)
        toast.info('Upload cancelled.')
      } catch (error: any) {
        console.error('Error cancelling upload:', error)
        toast.error('Failed to cancel upload')
        return
      }
    } else {
      toast.info('Upload cleared. You can start a new upload.')
    }
    resetUploadState()
  }

  const handleBack = () => {
    if (isUploadActive) {
      toast.warning('An upload is already in progress. Please wait for it to complete.')
      return
    }
    if (currentStep === 'PREVIEW') {
      setCurrentStep('UPLOAD')
      setPreview(null)
      setPreviewRows([])
      setPreviewRowsPage(1)
      setPreviewRowsTotal(0)
    } else if (currentStep === 'RESULT') {
      setCurrentStep('PREVIEW')
      setUploadResult(null)
    }
  }

  const loadPreviewRowsPage = async (page: number) => {
    if (!preview?.previewId) return
    setPreviewRowsLoading(true)
    try {
      const pageData: MachineryUploadPreviewRowsPage = await fetchMachineryPreviewRows(
        preview.previewId,
        page,
        previewRowsPageSize
      )
      if (pageData.expired) {
        toast.error('Preview session expired. Please run Preview again.')
        return
      }
      setPreviewRows(pageData.rows || [])
      setPreviewRowsPage(pageData.page)
      setPreviewRowsTotal(pageData.total)
    } catch (error: any) {
      console.error('Error loading preview rows:', error)
      toast.error('Failed to load preview rows')
    } finally {
      setPreviewRowsLoading(false)
    }
  }

  const buildErrorExportRows = (errors: BulkUploadError[]) => {
    return errors.map((error, idx) => {
      const rawMessage = error.message || ''
      const isWarning = rawMessage.toLowerCase().startsWith('warning:')
      const message = isWarning ? rawMessage.replace(/^warning:\s*/i, '') : rawMessage
      return {
        'Sr No': idx + 1,
        Row: error.rowNumber,
        Type: isWarning ? 'Warning' : 'Error',
        Message: message,
      }
    })
  }

  const downloadErrorsExcel = async (
    errors: BulkUploadError[],
    filenamePrefix = 'machinery_upload_errors'
  ) => {
    const nonWarningErrors = errors.filter((error) => !isWarningMessage(error.message))
    if (!nonWarningErrors.length) {
      toast.error('No errors to export')
      return
    }
    try {
      const XLSX = await import('xlsx')
      const rows = buildErrorExportRows(nonWarningErrors)
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Errors')
      const date = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(workbook, `${filenamePrefix}_${date}.xlsx`)
      toast.success('Errors exported to Excel')
    } catch (error) {
      console.error('Excel export failed:', error)
      toast.error('Failed to export errors to Excel')
    }
  }

  const handleDownloadErrorsExcel = async () => {
    if (!preview) {
      toast.error('No errors to export')
      return
    }
    await downloadErrorsExcel(preview.errors, 'machinery_upload_errors_preview')
  }

  const handleDownloadErrorsPdf = async () => {
    if (!preview) {
      toast.error('No errors to export')
      return
    }
    const nonWarningErrors = preview.errors.filter((error) => !isWarningMessage(error.message))
    if (nonWarningErrors.length === 0) {
      toast.error('No errors to export')
      return
    }
    try {
      const [{default: jsPDF}] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
      const rows = buildErrorExportRows(nonWarningErrors)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      })
      doc.setFontSize(14)
      doc.text('Machinery Upload Errors', 40, 40)
      doc.setFontSize(10)
      doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 58)
      ;(doc as any).autoTable({
        startY: 75,
        head: [['Sr No', 'Row', 'Type', 'Message']],
        body: rows.map((row) => [row['Sr No'], row.Row, row.Type, row.Message]),
        styles: {fontSize: 9, cellPadding: 3, overflow: 'linebreak'},
        headStyles: {fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold'},
        alternateRowStyles: {fillColor: [250, 250, 250]},
        margin: {top: 75, left: 10, right: 10},
      })

      const date = new Date().toISOString().slice(0, 10)
      doc.save(`machinery_upload_errors_${date}.pdf`)
      toast.success('Errors exported to PDF')
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.error('Failed to export errors to PDF')
    }
  }

  const buildWarningExportRows = (warnings: BulkUploadError[]) => {
    return warnings.map((warning, idx) => {
      const rawMessage = warning.message || ''
      const message = rawMessage.replace(/warning:\s*/i, '')
      return {
        'Sr No': idx + 1,
        Row: warning.rowNumber,
        Type: 'Warning',
        Message: message,
      }
    })
  }

  const handleDownloadWarningsExcel = async () => {
    if (!preview) {
      toast.error('No warnings to export')
      return
    }
    const source =
      (preview.warnings && preview.warnings.length > 0
        ? preview.warnings
        : preview.errors.filter((e) => isWarningMessage(e.message))) || []
    if (!source.length) {
      toast.error('No warnings to export')
      return
    }
    try {
      const XLSX = await import('xlsx')
      const rows = buildWarningExportRows(source)
      const worksheet = XLSX.utils.json_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Warnings')
      const date = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(workbook, `machinery_upload_warnings_${date}.xlsx`)
      toast.success('Warnings exported to Excel')
    } catch (error) {
      console.error('Excel export failed:', error)
      toast.error('Failed to export warnings to Excel')
    }
  }

  const handleDownloadWarningsPdf = async () => {
    if (!preview) {
      toast.error('No warnings to export')
      return
    }
    const source =
      (preview.warnings && preview.warnings.length > 0
        ? preview.warnings
        : preview.errors.filter((e) => isWarningMessage(e.message))) || []
    if (!source.length) {
      toast.error('No warnings to export')
      return
    }
    try {
      const [{default: jsPDF}] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
      const rows = buildWarningExportRows(source)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      })
      doc.setFontSize(14)
      doc.text('Machinery Upload Warnings', 40, 40)
      doc.setFontSize(10)
      doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 58)
      ;(doc as any).autoTable({
        startY: 75,
        head: [['Sr No', 'Row', 'Type', 'Message']],
        body: rows.map((row) => [row['Sr No'], row.Row, row.Type, row.Message]),
        styles: {fontSize: 9, cellPadding: 3, overflow: 'linebreak'},
        headStyles: {fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold'},
        alternateRowStyles: {fillColor: [250, 250, 250]},
        margin: {top: 75, left: 10, right: 10},
      })

      const date = new Date().toISOString().slice(0, 10)
      doc.save(`machinery_upload_warnings_${date}.pdf`)
      toast.success('Warnings exported to PDF')
    } catch (error) {
      console.error('PDF export failed:', error)
      toast.error('Failed to export warnings to PDF')
    }
  }

  if (!visible) return null

  if (!canImportMachinery) {
    return (
      <div
        style={WRAP_STYLE}
        tabIndex={-1}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className='bg-white text-dark rounded shadow-lg p-4'
          style={{maxWidth: 440}}
          onClick={(e) => e.stopPropagation()}
        >
          <h5 className='mb-2'>Machinery Bulk Upload</h5>
          <p className='text-muted mb-3'>You do not have permission to import machinery data.</p>
          <button type='button' className='btn btn-primary btn-sm' onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{width: '90vw', maxWidth: '1200px', maxHeight: '90vh'}}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <h5 className='modal-title text-dark m-0'>Machinery Bulk Upload</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>

        {/* Body */}
        <div className='flex-grow-1 overflow-auto px-4 py-3'>
          {currentStep === 'UPLOAD' && (
            <div className='row g-4'>
              {isUploadActive && (
                <div className='col-12'>
                  <div className='alert alert-warning mb-0'>
                    An upload is already in progress. You can view its progress here, but you cannot
                    start a new upload until it completes.
                  </div>
                </div>
              )}
              {/* Vessel Selection */}
              <div className='col-12'>
                <label className={LABEL}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                {isVesselOnlyUser ? (
                  <input
                    type='text'
                    className='form-control'
                    value={currentUser?.vessel?.fleet_name || 'Vessel'}
                    disabled
                  />
                ) : (
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId || ''}
                    onChange={(e) => setSelectedVesselId(Number(e.target.value))}
                    disabled={isUploadActive}
                  >
                    <option value=''>Select Vessel</option>
                    {vessels.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                )}
              </div>

              {/* File Selection */}
              <div className='col-12'>
                <label className={LABEL}>
                  Upload File <span className='text-danger'>*</span>
                </label>
                <input
                  type='file'
                  className='form-control'
                  accept='.xlsx,.xls,.csv'
                  onChange={handleFileSelect}
                  disabled={isUploadActive}
                />
                <div className='form-text'>
                  Supported formats: Excel (.xlsx, .xls) or CSV (.csv)
                </div>
              </div>

              {/* Template Download */}
              {!isVesselOnlyUser && (
                <div className='col-12'>
                  <button
                    type='button'
                    className='btn btn-light-primary btn-sm'
                    onClick={handleDownloadTemplate}
                  >
                    <KTSVG
                      path='/media/icons/duotune/files/fil021.svg'
                      className='svg-icon-3 me-2'
                    />
                    Download Template
                  </button>
                  <div className='form-text mt-2'>
                    Template includes 4 tabs: Vessel Locations, Jobs Library, Vessel Multiplicities,
                    and Spares. Use Location Code + Subsystem Code to link Vessel Multiplicities to
                    Vessel Locations, and System Code + SubComponent Code to link Jobs Library. Use
                    the optional Instance Of column in Vessel Locations to group similar systems.
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 'PREVIEW' && preview && (
            <div>
              <div className='alert alert-info'>
                <h6 className='mb-2'>Preview: What will be imported</h6>
                <p className='mb-2'>
                  Review the items below. Click <strong>Confirm & Import</strong> to proceed or{' '}
                  <strong>Cancel</strong> to go back and correct your file.
                </p>
                <p className='mb-0'>
                  <strong>Note:</strong> All changes are committed{' '}
                  <span className='badge badge-success'>atomically</span> - if any error occurs
                  during import, no data will be written. This ensures data consistency.
                </p>
              </div>

              <div className='row mb-3'>
                <div className='col-md-3'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-primary'>{preview.totalRows}</div>
                    <div className='text-muted'>Total Rows</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-success'>{preview.validRows}</div>
                    <div className='text-muted'>Valid Rows</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-danger'>{preview.errorRows}</div>
                    <div className='text-muted'>Error Rows</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-warning'>
                      {preview.warningRows ?? previewWarnings.length}
                    </div>
                    <div className='text-muted'>Warning Rows</div>
                  </div>
                </div>
              </div>

              {previewErrorRows.length > 0 && (
                <div className='alert alert-danger mb-3'>
                  <div className='d-flex flex-wrap justify-content-between align-items-center mb-2'>
                    <h6 className='mb-0'>Errors Found:</h6>
                    <div className='d-flex gap-2'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={handleDownloadErrorsExcel}
                      >
                        Download Excel
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-danger'
                        onClick={handleDownloadErrorsPdf}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                  <div className='table-responsive' style={{maxHeight: '200px', overflowY: 'auto'}}>
                    <table className='table table-sm table-bordered mb-0'>
                      <thead>
                        <tr>
                          <th>Sr No</th>
                          <th>Row</th>
                          <th>Type</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewErrorRows.map((error, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{error.rowNumber}</td>
                            <td>
                              <span className='badge badge-light-danger'>Error</span>
                            </td>
                            <td className='text-danger'>{error.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previewWarnings.length > 0 && (
                <div className='alert alert-warning mb-3'>
                  <div className='d-flex flex-wrap justify-content-between align-items-center mb-2'>
                    <h6 className='mb-0'>Warnings Found:</h6>
                    <div className='d-flex gap-2'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={handleDownloadWarningsExcel}
                      >
                        Download Excel
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light-warning'
                        onClick={handleDownloadWarningsPdf}
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                  <div className='table-responsive' style={{maxHeight: '200px', overflowY: 'auto'}}>
                    <table className='table table-sm table-bordered mb-0'>
                      <thead>
                        <tr>
                          <th>Sr No</th>
                          <th>Row</th>
                          <th>Type</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewWarnings.map((warning, idx) => {
                          const message = (warning.message || '').replace(/warning:\s*/i, '')
                          return (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>{warning.rowNumber}</td>
                              <td>
                                <span className='badge badge-light-warning'>Warning</span>
                              </td>
                              <td className='text-warning'>{message}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {previewRows.length > 0 && (
                <div>
                  <h6 className='mb-2'>Preview of Changes:</h6>
                  <div className='table-responsive' style={{maxHeight: '400px', overflowY: 'auto'}}>
                    <table className='table table-sm table-bordered'>
                      <thead>
                        <tr>
                          <th>Row</th>
                          <th>Action</th>
                          <th>Type</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Parent</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewRows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.rowNumber > 0 ? row.rowNumber : '-'}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.action === 'CREATE' ? 'badge-success' : 'badge-warning'
                                }`}
                              >
                                {row.action}
                              </span>
                            </td>
                            <td>{row.entityType}</td>
                            <td>{row.entityName}</td>
                            <td>{row.entityCode || '-'}</td>
                            <td>{row.parentEntity || '-'}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.status === 'NEW' ? 'badge-light-primary' : 'badge-light-info'
                                }`}
                              >
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className='d-flex justify-content-between align-items-center mt-2'>
                    <div className='text-muted'>
                      {previewRowsTotal > 0
                        ? `Showing ${(previewRowsPage - 1) * previewRowsPageSize + 1}-${Math.min(
                            previewRowsPage * previewRowsPageSize,
                            previewRowsTotal
                          )} of ${previewRowsTotal}`
                        : 'No rows'}
                    </div>
                    <div className='btn-group'>
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        disabled={previewRowsLoading || previewRowsPage <= 1}
                        onClick={() => loadPreviewRowsPage(previewRowsPage - 1)}
                      >
                        Previous
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-light'
                        disabled={
                          previewRowsLoading ||
                          previewRowsPage * previewRowsPageSize >= previewRowsTotal
                        }
                        onClick={() => loadPreviewRowsPage(previewRowsPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {currentStep === 'RESULT' && uploadJob && (
            <div>
              <div
                className={`alert ${
                  uploadJob.status === 'FAILED'
                    ? 'alert-danger'
                    : uploadJob.status === 'SUCCESS'
                    ? 'alert-success'
                    : uploadJob.status === 'CANCELLED'
                    ? 'alert-warning'
                    : 'alert-info'
                }`}
              >
                <h6 className='mb-2'>
                  {uploadJob.status === 'SUCCESS'
                    ? 'Upload Complete'
                    : uploadJob.status === 'FAILED'
                    ? 'Upload Failed'
                    : uploadJob.status === 'CANCELLED'
                    ? 'Upload Cancelled'
                    : 'Upload In Progress'}
                </h6>
                <p className='mb-0'>
                  {uploadJob.status === 'QUEUED' || uploadJob.status === 'RUNNING'
                    ? inProgressMessage
                    : uploadJob.message || 'Upload status updated.'}
                </p>
              </div>

              {(uploadJob.status === 'QUEUED' || uploadJob.status === 'RUNNING') && (
                <div className='mb-3'>
                  <div className='progress' style={{height: 10}}>
                    <div
                      className='progress-bar bg-primary'
                      role='progressbar'
                      style={{width: `${uploadJob.progressPercent ?? 0}%`}}
                    />
                  </div>
                  <div className='small text-muted mt-2'>
                    {uploadJob.progressPercent ?? 0}%{' '}
                    {uploadJob.etaSeconds ? `- ETA ${formatEta(uploadJob.etaSeconds)}` : ''}
                    {uploadJob.totalRows
                      ? ` - ${uploadJob.processedRows ?? 0}/${uploadJob.totalRows} rows`
                      : ''}
                  </div>
                </div>
              )}

              {uploadJob.status === 'SUCCESS' && uploadJobErrors.length > 0 && (
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-primary'
                    onClick={() => setShowJobErrors((prev) => !prev)}
                  >
                    {showJobErrors ? 'Hide Error List' : 'Show Error List'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-secondary'
                    onClick={() => downloadErrorsExcel(uploadJobErrors, 'machinery_upload_errors')}
                  >
                    Download Errors
                  </button>
                </div>
              )}

              {showJobErrors && uploadJobErrors.length > 0 && (
                <div className='table-responsive' style={{maxHeight: '300px', overflowY: 'auto'}}>
                  <table className='table table-sm table-bordered'>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadJobErrors.map((error, idx) => (
                        <tr key={idx}>
                          <td>{error.rowNumber}</td>
                          <td className='text-danger'>{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {currentStep === 'RESULT' && uploadResult && !uploadJob && (
            <div>
              <div
                className={`alert ${
                  uploadResult.errorCount === 0 ? 'alert-success' : 'alert-warning'
                }`}
              >
                <h6 className='mb-2'>Upload Complete</h6>
                <p className='mb-0'>
                  {uploadResult.errorCount === 0
                    ? `Successfully imported ${uploadResult.successCount} rows.`
                    : `Import completed with ${uploadResult.errorCount} errors out of ${uploadResult.totalRows} rows.`}
                </p>
              </div>

              <div className='row mb-3'>
                <div className='col-md-4'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-primary'>{uploadResult.totalRows}</div>
                    <div className='text-muted'>Total Rows</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-success'>{uploadResult.successCount}</div>
                    <div className='text-muted'>Success</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='text-center p-3 bg-light rounded'>
                    <div className='fs-3 fw-bold text-danger'>{uploadResult.errorCount}</div>
                    <div className='text-muted'>Errors</div>
                  </div>
                </div>
              </div>

              {uploadResultErrors.length > 0 && (
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-primary'
                    onClick={() => setShowResultErrors((prev) => !prev)}
                  >
                    {showResultErrors ? 'Hide Error List' : 'Show Error List'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-secondary'
                    onClick={() =>
                      downloadErrorsExcel(uploadResultErrors, 'machinery_upload_errors')
                    }
                  >
                    Download Errors
                  </button>
                </div>
              )}

              {showResultErrors && uploadResultErrors.length > 0 && (
                <div className='table-responsive' style={{maxHeight: '300px', overflowY: 'auto'}}>
                  <table className='table table-sm table-bordered'>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResultErrors.map((error, idx) => (
                        <tr key={idx}>
                          <td>{error.rowNumber}</td>
                          <td className='text-danger'>{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='d-flex align-items-center justify-content-between border-top px-4 py-3 flex-shrink-0'>
          <div>
            {currentStep !== 'UPLOAD' && (
              <button
                type='button'
                className='btn btn-light'
                onClick={handleBack}
                disabled={previewing || uploading || isUploadActive}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2 me-1' />
                Back
              </button>
            )}
          </div>
          <div className='d-flex gap-2'>
            <button
              type='button'
              className={isUploadActive ? 'btn btn-danger' : 'btn btn-secondary'}
              onClick={uploadJob ? handleCancelUpload : onClose}
              disabled={previewing || uploading}
            >
              {isUploadActive ? 'Cancel Upload' : uploadJob ? 'Clear Upload' : 'Cancel'}
            </button>
            {currentStep === 'UPLOAD' && (
              <button
                type='button'
                className='btn btn-primary'
                onClick={handlePreview}
                disabled={!selectedFile || !selectedVesselId || previewing || isUploadActive}
              >
                {previewing ? 'Previewing...' : 'Preview'}
              </button>
            )}
            {currentStep === 'PREVIEW' && (
              <button
                type='button'
                className='btn btn-success'
                onClick={handleConfirmUpload}
                disabled={uploading || isUploadActive || (preview ? preview.errorRows > 0 : false)}
              >
                {uploading ? 'Importing...' : 'Confirm & Import'}
              </button>
            )}
            {currentStep === 'RESULT' && (
              <button type='button' className='btn btn-primary' onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
